import { COMING_SOON_FEATURES } from '../lib/constants';
import { ImageDimensions, MockDimensions, UtmParams } from '../types';
import { CropArea } from '~/components/PostMediaEditor';
import { convertToInches } from '@arthelper/shared/dimensions';
import { appendAttributionToUrl, getFirstTouchAttribution, getRawAttribution } from '@arthelper/shared/attribution';
import { sanitizeRedirectPath } from '@arthelper/shared/safe-redirect';
import { DEFAULT_LOCALE, isLocale } from '@arthelper/shared/i18n';

export function isGrowSurfEnabled() {
  // const isDevelopment = process.env.NODE_ENV === 'development';
  // const isProdLikeEnv = ['staging', 'production'].includes(__STAGE__);

  // return (isDevelopment || isProdLikeEnv) && __GSC__;

  // ART-8107 - Disable GrowSurf
  return false;
}

export function cleanHtmlResponse(htmlString: string | null, removeLinks: boolean = false) {
  if (!htmlString) {
    return '';
  }

  if (removeLinks) {
    // Remove all links
    htmlString = htmlString.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>.*?<\/a>/gi, '');
  } else {
    // Replace all links with their href values
    htmlString = htmlString.replace(/<a[^>]*href=["']([^"']*)["'][^>]*>.*?<\/a>/gi, '$1');
  }

  const blockElements = ['div', 'p', 'br', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'ul', 'ol', 'strong'];
  blockElements.forEach((tag) => {
    const regex = new RegExp(`<${tag}[^>]*>`, 'gi');
    htmlString = htmlString ? htmlString.replace(regex, '\n') : '';
    htmlString = htmlString ? htmlString.replace(new RegExp(`</${tag}>`, 'gi'), '\n') : '';
  });

  // Remove any remaining HTML tags
  htmlString = htmlString.replace(/<[^>]+>/g, '');

  // Replace multiple newlines with a single newline
  htmlString = htmlString.replace(/\n{3,}/g, '\n\n');

  return htmlString.trim();
}

interface TypewriterOptions {
  html: string;
  onUpdate: (text: string) => void;
  onComplete?: () => void;
  typingSpeed?: number;
}

export function animateTypewriter({ html, onUpdate, onComplete, typingSpeed = 18 }: TypewriterOptions) {
  // Split HTML into chunks that preserve tags and content
  const chunks = html.split(/(<[^>]*>)/g).filter(Boolean);
  let currentIndex = 0;
  let accumulatedText = '';
  let textBuffer = '';

  onUpdate(''); // Start with empty response

  const processNextChunk = () => {
    // Process all consecutive HTML tags at once
    while (currentIndex < chunks.length && chunks[currentIndex].startsWith('<')) {
      accumulatedText += chunks[currentIndex];
      currentIndex++;
    }

    // If we've reached the end after processing tags
    if (currentIndex >= chunks.length) {
      onComplete?.();
      return;
    }

    // Process text content
    const textChunk = chunks[currentIndex];
    const charsPerFrame = Math.ceil(textChunk.length / 30); // Adjusted for smoother typing
    textBuffer = textChunk;
    let processedChars = 0;

    const typeCharacters = () => {
      if (processedChars >= textBuffer.length) {
        currentIndex++;
        processNextChunk();
        return;
      }

      // Add next batch of characters
      const nextChars = textBuffer.slice(processedChars, processedChars + charsPerFrame);
      accumulatedText += nextChars;
      processedChars += charsPerFrame;
      onUpdate(accumulatedText);

      // Schedule next batch
      setTimeout(() => {
        requestAnimationFrame(typeCharacters);
      }, typingSpeed);
    };

    typeCharacters();
  };

  // Start the animation
  requestAnimationFrame(processNextChunk);
}

// Normalize away any trailing slash so the templates below can always use an
// explicit `/` separator. SST Lambda function URLs end in `/`; the local
// fallback `http://localhost:3033` does not — without this you'd get
// `http://localhost:30331000000/...` (host smashed against asfId) locally,
// or `https://xxx.lambda-url.../1000000/...` (double slash) in the cloud.
export const IMAGE_SERVER_URL = (import.meta.env.VITE_MOCKS_LAMBDA_URL || __MOCKS_LAMBDA_URL__)?.replace(/\/$/, '');

export function getMockImageUrl(
  asfId: string,
  mockUuid: string,
  imageId: number,
  imageDimensions: ImageDimensions,
  mockDimensions: MockDimensions
) {
  if (
    imageDimensions.imageWidth &&
    imageDimensions.imageHeight &&
    imageDimensions.imageDimensionType &&
    mockDimensions.mockScaleFactor
  ) {
    const { width, height } = convertToInches(
      imageDimensions.imageWidth,
      imageDimensions.imageHeight,
      imageDimensions.imageDimensionType
    );
    const normalizedWidth = parseFloat(width.toFixed(1));
    const normalizedHeight = parseFloat(height.toFixed(1));
    const normalizedScaleFactor = parseFloat(mockDimensions.mockScaleFactor.toFixed(2));

    return `${IMAGE_SERVER_URL}/${asfId}/${mockUuid}/${imageId}/w${normalizedWidth}_h${normalizedHeight}_s${normalizedScaleFactor}`;
  }

  return `${IMAGE_SERVER_URL}/${asfId}/${mockUuid}/${imageId}`;
}

// Build the public URL for a generated mock. Host comes from the caller
// (`bucketUrl`), so this is not tied to S3 — pointing it at a CDN in front of
// the bucket needs no change here.
export function getMockUrl(
  asfId: string,
  mockUuid: string,
  imageId: number,
  bucketUrl: string,
  imageDimensions: ImageDimensions,
  mockDimensions: MockDimensions,
  width?: number,
  height?: number
) {
  let url;

  if (
    imageDimensions.imageWidth &&
    imageDimensions.imageHeight &&
    imageDimensions.imageDimensionType &&
    mockDimensions.mockScaleFactor
  ) {
    const { width, height } = convertToInches(
      imageDimensions.imageWidth,
      imageDimensions.imageHeight,
      imageDimensions.imageDimensionType
    );
    const normalizedWidth = parseFloat(width.toFixed(1));
    const normalizedHeight = parseFloat(height.toFixed(1));
    const normalizedScaleFactor = parseFloat(mockDimensions.mockScaleFactor.toFixed(2));

    url = `https://${bucketUrl}/mocks/${asfId}/${imageId}/${mockUuid}/w${normalizedWidth}_h${normalizedHeight}_s${normalizedScaleFactor}/o`;
  } else {
    url = `https://${bucketUrl}/mocks/${asfId}/${imageId}/${mockUuid}/o`;
  }

  if (width && height) {
    url += `_w${width}`;
  }

  if (height) {
    url += `_h${height}`;
  }

  url += '.jpeg';
  return url;
}

const isComingSoon = (feature: string) => {
  return COMING_SOON_FEATURES.includes(feature);
};

// Get the base URL for the public site (town-square)
export function getPublicSiteBaseUrl() {
  return __TOWN_SQUARE_URL__;
}

// Town-square is a next-intl app configured with `localePrefix: 'as-needed'`
// (see town-square/src/i18n/routing.ts): the default locale (`en`) has no
// path prefix, every other supported locale lives under `/{locale}/...`.
//
// Cross-app navigation from the client (a Vite SPA) can't go through
// next-intl's locale-aware router, and the client's i18n state lives in
// localStorage (`preferred-locale`, set by client/src/i18n.ts) — NOT in the
// NEXT_LOCALE cookie that town-square's middleware reads. Without an
// explicit prefix on outbound links, a user with `preferred-locale: es`
// will land on the English page (or take an extra middleware redirect via
// the `cf-ipcountry` fallback, which is wrong any time the GeoIP country
// and the manually selected locale disagree).
//
// This helper reads the same localStorage key i18next writes, falls back
// safely when the value is missing / unsupported / unreadable, and
// produces a fully-qualified URL that hits the correct locale on the
// first request — no middleware redirect.
export function buildTownSquareUrl(path: string): string {
  const base = __TOWN_SQUARE_URL__ || 'https://www.arthelper.com';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const locale = readPreferredLocale();
  const localePrefix = locale && locale !== DEFAULT_LOCALE ? `/${locale}` : '';
  return `${base}${localePrefix}${normalizedPath}`;
}

function readPreferredLocale(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = window.localStorage.getItem('preferred-locale');
    return value && isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Redirects to the signin page on the town-square domain
 * @param redirectPath - Optional path to redirect to after signin (relative to current origin)
 */
export function redirectToSignIn(redirectPath?: string) {
  const safePath = sanitizeRedirectPath(redirectPath);
  const returnUrl = redirectPath
    ? `${window.location.origin}${safePath}`
    : `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;

  const signInUrl = new URL('/signin', __TOWN_SQUARE_URL__);
  // Append attribution to the post-auth return URL so it survives the bounce
  // back to the client app (cross-domain; localStorage cannot bridge
  // .arthelper.com -> .arthelper.ai).
  signInUrl.searchParams.set('redirect_url', appendAttributionToUrl(returnUrl));
  // Also append attribution onto the town-square sign-in URL itself so the
  // page mount captures it and forwards via Clerk metadata + post-auth redirect.
  window.location.replace(appendAttributionToUrl(signInUrl.toString()));
}

/**
 * Redirects to the signup page on the town-square domain
 * @param redirectPath - Optional path to redirect to after signup (relative to current origin)
 */
export function redirectToSignUp(redirectPath?: string) {
  const safePath = sanitizeRedirectPath(redirectPath);
  const returnUrl = redirectPath
    ? `${window.location.origin}${safePath}`
    : `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;

  const signUpUrl = new URL('/signup', __TOWN_SQUARE_URL__);
  signUpUrl.searchParams.set('redirect_url', appendAttributionToUrl(returnUrl));
  window.location.replace(appendAttributionToUrl(signUpUrl.toString()));
}

// Generate public image URL for town-square
export function getPublicImageUrl(userSlug: string, imageSlug: string) {
  return `${getPublicSiteBaseUrl()}/${userSlug}/image/${imageSlug}`;
}

const hasComingSoonFeatures = (features: string[]) => features.some((feature: string) => isComingSoon(feature));

export function mapStripePricesToPlans(prices: any[], currentPlan: string | null) {
  const groupedByProduct = prices.reduce((acc, price) => {
    const productName = price.product.name;
    const marketingFeatures = price.product.marketing_features;

    if (!acc[productName]) {
      acc[productName] = {
        id: price.product.id,
        name: productName,
        features: marketingFeatures.map((f: any) => ({
          name: f.name,
          isComingSoon: isComingSoon(f.name),
        })),
        monthly: 0,
        yearly: 0,
        monthlyPriceId: '',
        yearlyPriceId: '',
        hasComingSoonFeatures: hasComingSoonFeatures(marketingFeatures.map((f: any) => f.name)),
      };
    }

    const amount = price.unit_amount / 100;
    if (amount > 100) {
      acc[productName].yearly = amount / 12;
      acc[productName].yearlyPriceId = price.id;
    } else {
      acc[productName].monthly = amount;
      acc[productName].monthlyPriceId = price.id;
    }

    return acc;
  }, {});

  // Define plan order for comparison
  const planOrder = ['Free', 'Growth', 'Pro'];

  return Object.values(groupedByProduct)
    .sort((a: any, b: any) => a.monthly - b.monthly)
    .map((plan: any) => ({
      ...plan,
      isCurrent: currentPlan ? plan.name.toLowerCase() === currentPlan.toLowerCase() : plan.name === 'Free',
      isPopular: plan.name === 'Pro',
      previousTier: plan.name === 'Growth' ? 'Free' : plan.name === 'Pro' ? 'Growth' : undefined,
      // Add tierLevel for comparison
      tierLevel: planOrder.indexOf(plan.name),
      currentTierLevel: currentPlan ? planOrder.indexOf(currentPlan) : 0,
    }));
}

export const formatDate = (date: Date, format: 'datetime' | 'date' | 'time' = 'datetime') => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: format !== 'date' ? 'numeric' : undefined,
    minute: format !== 'date' ? 'numeric' : undefined,
  };

  return date.toLocaleString(undefined, options);
};

export const validateTagName = (tag: string): string | null => {
  // Check length
  if (tag.length < 3) {
    return 'Tag must be at least 3 characters long';
  }
  if (tag.length > 255) {
    return 'Tag cannot exceed 255 characters';
  }

  // Check characters using regex
  if (!/^[a-zA-Z0-9\- ]+$/.test(tag)) {
    return 'Tag can only contain letters, numbers, hyphens and spaces';
  }

  return null;
};

export const getUtmParams = (profile: any): UtmParams => {
  // Route both reads through the shared module so the same sanitization that
  // gates writes also gates reads. Bypassing it (raw JSON.parse) would let any
  // unsanitized values lingering in localStorage — legacy entries, third-party
  // writes, manual injection — leak straight into Intercom/Amplitude payloads.
  const currentParams = getRawAttribution();
  const firstVisitParams = getFirstTouchAttribution();

  // First-touch (`first_*`-prefixed) keys are emitted only when the profile
  // doesn't yet have a firstUtmSource on file, matching the prior behavior.
  const out: UtmParams = { ...currentParams };
  if (Object.keys(firstVisitParams).length > 0 && !profile?.firstUtmSource) {
    for (const [key, value] of Object.entries(firstVisitParams)) {
      out[`first_${key}`] = value;
    }
  }
  return out;
};

export const dataURLToBlob = (dataURL: string): Promise<Blob> => {
  return new Promise((resolve) => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    resolve(new Blob([u8arr], { type: mime }));
  });
};

export const uploadToS3 = (blob: Blob, signedUrl: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', signedUrl, true);
    xhr.setRequestHeader('Content-Type', blob.type);
    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('XHR error'));
    xhr.send(blob);
  });
};

export const generateCroppedImages = async (
  images: string[],
  cropAreas: Record<string, CropArea>,
  selectedRatio: { value: number },
  backgroundColor: Record<string, string>,
  imageIds?: string[]
): Promise<string[]> => {
  const createCroppedImage = (source: string, pixelCrop: CropArea | null, index: number): Promise<string | null> => {
    return new Promise((resolve) => {
      const image = new Image();

      // Only set crossOrigin for remote URLs, not for data URLs
      if (!source.startsWith('data:')) {
        image.crossOrigin = 'anonymous';
        source = source.includes('?') ? `${source}&nocache=${Date.now()}` : `${source}?nocache=${Date.now()}`;
      }

      image.onload = () => {
        const canvas = document.createElement('canvas');
        let finalCrop: CropArea;

        // If no crop area is provided or has invalid dimensions, create a default one that maintains aspect ratio
        if (!pixelCrop || (pixelCrop.width === 0 && pixelCrop.height === 0)) {
          const aspectRatio = selectedRatio.value; // Use the selected aspect ratio instead of default
          let width = image.width;
          let height = width / aspectRatio;

          // If height is too large, scale down from height instead
          if (height > image.height) {
            height = image.height;
            width = height * aspectRatio;
          }

          // Center the crop
          finalCrop = {
            x: Math.max(0, Math.floor((image.width - width) / 2)),
            y: Math.max(0, Math.floor((image.height - height) / 2)),
            width: Math.min(Math.floor(width), image.width),
            height: Math.min(Math.floor(height), image.height),
          };
        } else {
          finalCrop = pixelCrop;
        }

        // Ensure we have valid dimensions
        if (finalCrop.width <= 0 || finalCrop.height <= 0) {
          console.error(`Invalid crop dimensions for image ${index}, using full image`);
          finalCrop = {
            x: 0,
            y: 0,
            width: image.width,
            height: image.height,
          };
        }

        // Scale down the output if either dimension exceeds 1080px
        const maxWidth = 1080;
        let outputWidth = finalCrop.width;
        let outputHeight = finalCrop.height;

        // For reels (9:16), maintain width at 1080px and calculate height
        if (selectedRatio.value === 9 / 16) {
          if (outputWidth > maxWidth) {
            const scale = maxWidth / outputWidth;
            outputWidth = maxWidth;
            outputHeight = Math.floor(outputHeight * scale);
          }
        } else {
          // For other aspect ratios, scale proportionally
          if (outputWidth > maxWidth || outputHeight > maxWidth) {
            const scale = Math.min(maxWidth / outputWidth, maxWidth / outputHeight);
            outputWidth = Math.floor(outputWidth * scale);
            outputHeight = Math.floor(outputHeight * scale);
          }
        }

        canvas.width = outputWidth;
        canvas.height = outputHeight;
        const ctx = canvas.getContext('2d');

        if (ctx) {
          try {
            // Get background color using imageId if available, otherwise fall back to index-based key
            const imageKey = imageIds && imageIds[index] ? imageIds[index] : `image-${index}`;
            ctx.fillStyle = backgroundColor[imageKey] || '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.drawImage(
              image,
              finalCrop.x,
              finalCrop.y,
              finalCrop.width,
              finalCrop.height,
              0,
              0,
              outputWidth,
              outputHeight
            );
            const result = canvas.toDataURL('image/jpeg', 0.95);

            resolve(result);
          } catch (error) {
            console.error(`Error cropping image ${index}:`, error);
            resolve(source);
          }
        } else {
          console.error(`Failed to get context for image ${index}`);
          resolve(source);
        }
      };

      image.onerror = () => {
        console.error('Error loading image:', source);
        // For failed images, return null so they can be filtered out
        resolve(null);
      };

      image.src = source;
    });
  };

  const croppedUrls: string[] = [];
  for (let i = 0; i < images.length; i++) {
    if (images[i]) {
      // Use imageId if available, otherwise fall back to index-based key
      const imageKey = imageIds && imageIds[i] ? imageIds[i] : `image-${i}`;
      const cropArea = cropAreas[imageKey] || null;
      const croppedUrl = await createCroppedImage(images[i], cropArea, i);
      // Only add non-null results (filter out failed images)
      if (croppedUrl) {
        croppedUrls.push(croppedUrl);
      }
    }
  }

  return croppedUrls;
};

export function possessive(name: string): string {
  return name.endsWith('s') ? `${name}'` : `${name}'s`;
}
